import { useParams } from '@reach/router';
import { graphql } from 'gatsby';
import gql from 'graphql-tag';
import React, { FC } from 'react';
import { feedback } from 'react-feedbacker';
import { useMutation, useQuery } from 'urql';

import { useFeedbackMessages } from '@/bits';
import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  Copy,
  Date as DateComponent,
  OptionsMenu,
  Value,
  renderOptionalValue,
} from '@/components';
import { EditIcon, RefreshIcon } from '@/components/icons';
import { TrashIcon } from '@/components/icons/TrashIcon';
import { useTranslate } from '@/contexts';
import { PlayerUpdateAffiliateInfoForm } from '@/forms';
import { BrandEnum } from '@/globalTypes';
import { useCan } from '@/utils/access';
import formatCountryCode from '@/utils/formatter/formatCountryCode';
import formatDate from '@/utils/formatter/formatDate';
import { getOperatorFromEnv } from '@/utils/operators';
import {
  PlayerActivityInfoBoxQuery,
  PlayerActivityInfoBoxQueryVariables,
  RemoveAffiliateParametersMutation,
  RemoveAffiliateParametersMutationVariables,
  ResetGlobalInvalidLoginAttemptsLeftMutation,
  ResetGlobalInvalidLoginAttemptsLeftMutationVariables,
} from './__generated__/component';

export const Fragment = graphql`
  fragment SanityPlayerActivityInfoBlock on SanityPlayerActivityInfoBlock {
    title {
      ...SanityLocaleString
    }
    edit {
      ...SanityLocaleString
    }
    resetLoginAttempts {
      ...SanityLocaleString
    }
    registered {
      ...SanityLocaleString
    }
    registrationIp {
      ...SanityLocaleString
    }
    lastLogin {
      ...SanityLocaleString
    }
    lastLoginIp {
      ...SanityLocaleString
    }
    loginAttemptsLeft {
      ...SanityLocaleString
    }
    reachedAt {
      ...SanityLocaleString
    }
    firstDeposit {
      ...SanityLocaleString
    }
    lastDeposit {
      ...SanityLocaleString
    }
    lastBonusAdjustment {
      ...SanityLocaleString
    }
    affiliateId {
      ...SanityLocaleString
    }
    affiliateToken {
      ...SanityLocaleString
    }
  }
`;

const QUERY = gql`
  query PlayerActivityInfoBox($playerId: ID!) {
    player(playerId: $playerId) {
      id
      uuid
      depositInfoV2 {
        first
        last
      }
      brand {
        id
        code
      }
      loginInfo {
        lastLogin {
          ip
          loggedAt
          countryCode
        }
        registration {
          ip
          loggedAt
          countryCode
        }
      }
      lastBonusAdjustment: transactionsV2(
        first: 1
        adjustmentCategory: Bonus
        orderBy: processedAt
        desc: true
      ) {
        edges {
          node {
            rawTransactionId
            processedAt
          }
        }
      }
      globalLoginAttemptsInfo {
        invalidGlobalLoginAttemptsAllowed
        maxGlobalLoginAttemptsReachedAt
      }
      affiliateInfo {
        affiliateToken
        affiliateId
      }
    }
  }
`;

const removeAffiliateParametersMutation = gql`
  mutation RemoveAffiliateParameters($playerId: ID!) {
    removeAffiliateParameters(playerId: $playerId) {
      id
      affiliateInfo {
        affiliateId
        affiliateToken
      }
    }
  }
`;

const resetGlobalInvalidLoginAttemptsLeft = gql`
  mutation ResetGlobalInvalidLoginAttemptsLeft(
    $playerId: ID!
    $brand: BrandEnum!
  ) {
    resetGlobalInvalidLoginAttemptsLeftV2(playerId: $playerId, brand: $brand) {
      id
      globalLoginAttemptsInfo {
        invalidGlobalLoginAttemptsAllowed
        maxGlobalLoginAttemptsReachedAt
      }
    }
  }
`;

const PlayerActivityInfoBlock: FC<{
  block: Queries.SanityPlayerActivityInfoBlockFragment;
}> = ({ block }) => {
  const { t } = useTranslate();
  const params = useParams();
  const feedbackMessages = useFeedbackMessages();
  const canManageAffiliates = useCan('MANAGE_AFFILIATES');

  const operator = getOperatorFromEnv();

  const affiliateDomain = operator?.affiliateDomain || '79affiliates.com';

  const [{ data, fetching }, refresh] = useQuery<
    PlayerActivityInfoBoxQuery,
    PlayerActivityInfoBoxQueryVariables
  >({
    query: QUERY,
    variables: {
      playerId: params.playerId,
    },
  });

  const [, removeAffiliateInfo] = useMutation<
    RemoveAffiliateParametersMutation,
    RemoveAffiliateParametersMutationVariables
  >(removeAffiliateParametersMutation);

  const [, resetInvalidLogins] = useMutation<
    ResetGlobalInvalidLoginAttemptsLeftMutation,
    ResetGlobalInvalidLoginAttemptsLeftMutationVariables
  >(resetGlobalInvalidLoginAttemptsLeft);

  const hasAffiliateInfo =
    !!data?.player.affiliateInfo?.affiliateId ||
    !!data?.player.affiliateInfo?.affiliateToken;

  const affiliateOptionsMenu = (
    <OptionsMenu
      items={[
        {
          content: t(block.edit),
          icon: <EditIcon />,
          modalContent: data?.player.id != null && (
            <PlayerUpdateAffiliateInfoForm playerId={data.player.id} />
          ),
        },
        {
          content: 'Remove all affiliate info',
          icon: <TrashIcon />,
          shouldConfirm: true,
          isHidden: !hasAffiliateInfo,
          onClick: () => {
            if (data?.player.id) {
              removeAffiliateInfo({
                playerId: data?.player.id,
              }).then((res) => {
                if (res.error?.message) {
                  feedback.error(res.error.message);
                } else {
                  feedback.success(t(feedbackMessages.success));
                }
              });
            }
          },
        },
      ]}
    />
  );

  const loginAttemptOptionsMenu = (
    <OptionsMenu
      items={[
        {
          content: t(block.resetLoginAttempts),
          icon: <RefreshIcon />,
          shouldConfirm: true,
          onClick: () => {
            if (data?.player) {
              resetInvalidLogins({
                playerId: data?.player.id,
                brand: data?.player.brand.code as BrandEnum, // Backend misaligment - should be fixed on their side
              }).then((res) => {
                if (res.error?.message) {
                  feedback.error(res.error.message);
                } else {
                  feedback.success(t(feedbackMessages.success));
                }
              });
            }
          },
        },
      ]}
    />
  );

  const lastBonusAdjustment = data?.player.lastBonusAdjustment.edges?.[0]?.node;

  return (
    <Card
      size="lg"
      title={
        <>
          {t(block.title)}{' '}
          <Copy fetching={fetching} value={data?.player.uuid} />
        </>
      }
      options={
        <CardOptions>
          <CardOptionsButton
            className="flex"
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
        </CardOptions>
      }
    >
      <CardBody>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          <Value fetching={fetching} title={t(block.registered)}>
            {formatCountryCode(
              data?.player?.loginInfo?.registration.countryCode,
            )}{' '}
            <DateComponent
              copyable
              date={data?.player?.loginInfo?.registration?.loggedAt}
            />
          </Value>
          <Value fetching={fetching} title={t(block.registrationIp)}>
            {formatCountryCode(
              data?.player?.loginInfo?.registration.countryCode,
            )}{' '}
            <Copy value={data?.player?.loginInfo?.registration?.ip} />
          </Value>
          <Value fetching={fetching} title={t(block.lastLogin)}>
            {formatCountryCode(data?.player?.loginInfo?.lastLogin.countryCode)}{' '}
            <DateComponent
              copyable
              date={data?.player?.loginInfo?.lastLogin?.loggedAt}
            />
          </Value>{' '}
          <Value fetching={fetching} title={t(block.lastLoginIp)}>
            {formatCountryCode(data?.player?.loginInfo?.lastLogin.countryCode)}{' '}
            <Copy value={data?.player?.loginInfo?.lastLogin?.ip} />
          </Value>
          <Value fetching={fetching} title={t(block.loginAttemptsLeft)}>
            {data?.player?.globalLoginAttemptsInfo
              ?.maxGlobalLoginAttemptsReachedAt ? (
              <>
                <Copy
                  value={`0, ${t(block.reachedAt, {
                    date: formatDate(
                      new Date(
                        data?.player?.globalLoginAttemptsInfo?.maxGlobalLoginAttemptsReachedAt,
                      ),
                    ),
                  })}`}
                />
                {loginAttemptOptionsMenu}
              </>
            ) : (
              <Copy
                value={renderOptionalValue(
                  data?.player?.globalLoginAttemptsInfo
                    ?.invalidGlobalLoginAttemptsAllowed,
                )}
              />
            )}
          </Value>
          <Value fetching={fetching} title={t(block.firstDeposit)}>
            <DateComponent copyable date={data?.player?.depositInfoV2?.first} />
          </Value>
          <Value fetching={fetching} title={t(block.lastDeposit)}>
            <DateComponent
              copyable
              date={data?.player?.depositInfoV2?.last}
              presentation="timeAgo"
            />
          </Value>
          <Value fetching={fetching} title={t(block.lastBonusAdjustment)}>
            <DateComponent copyable date={lastBonusAdjustment?.processedAt} />
          </Value>
          <Value
            fetching={fetching}
            title={t(block.affiliateId)}
            value={data?.player.affiliateInfo?.affiliateId}
            suffix={canManageAffiliates && affiliateOptionsMenu}
          >
            {data?.player.affiliateInfo?.affiliateId ? (
              <a
                href={`https://admin.${affiliateDomain}/search_results.php?search=true&search_user_id=${data?.player.affiliateInfo?.affiliateId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {data?.player.affiliateInfo?.affiliateId}
              </a>
            ) : (
              '-'
            )}
          </Value>
          <Value
            fetching={fetching}
            title={t(block.affiliateToken)}
            value={data?.player.affiliateInfo?.affiliateToken}
            suffix={canManageAffiliates && affiliateOptionsMenu}
          >
            {renderOptionalValue(data?.player.affiliateInfo?.affiliateToken)}
          </Value>
        </div>
      </CardBody>
    </Card>
  );
};

export default PlayerActivityInfoBlock;
